import { Alert, Clickable, Col, Divider, Icon, Row, Text } from 'components';
import { useOutsideClick } from 'core';
import { Api, theme } from 'lib';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { MuteOptions } from './';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px 16px;
  gap: 4px;
  width: 264px;
  height: 373px;

  position: absolute;
  right: -527px;
  top: 19px;

  background: #fff;
  box-shadow: 0px 0px 16px rgba(0, 0, 0, 0.1);
  border-radius: 24px;
  z-index: 3;

  @media (max-width: 1024px) {
    top: 30px;
    right: 10px;
  }
`;

const CloseMenu = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
`;

const Menu = ({ chat, user, visible, setVisible }) => {
  const [muteModal, setMuteModal] = useState(false),
    [alert, setAlert] = useState(''),
    dispatch = useDispatch(),
    navigate = useNavigate(),
    { isMobile } = useSelector((state) => state.appState),
    { t } = useTranslation(),
    chatSettings = chat.users.find((x) => x.user._id === user._id),
    compRef = useRef();

  useOutsideClick(compRef, () => visible && setVisible(false), [
    'chat-settings',
    'chat-mute-settings'
  ]);

  function handleMuteModal() {
    setMuteModal(!muteModal);
    setTimeout(() => setVisible(true), 100);
  }

  async function updateChatSettings(type, value) {
    const getMethod = () => {
      switch (type) {
        case 'hidden':
          return 'hide';

        case 'favorite':
          return 'favorite';

        case 'clearHistoryDate':
          return 'clearHistory';

        case 'deletedDate':
          return 'delete';

        case 'mutedUntil':
          return 'mute';
      }
    };

    const res = await Api.patch(`/chats/${getMethod()}`, { chatId: chat._id, value });
    if (!res) return;

    dispatch({
      type: 'UPDATE_CHAT_SETTINGS',
      payload: {
        chatId: chat._id,
        userId: user._id,
        type,
        value
      }
    });

    setVisible(false);
    if (type === 'deletedDate') navigate('/messages');
  }

  function alertUser(text) {
    setAlert(text);
  }

  function handleAccept() {
    setAlert('');

    switch (alert) {
      case t('chats.clearConfirmation'):
        return updateChatSettings('clearHistoryDate', new Date());

      case t('chats.deleteConfirmation'):
        return updateChatSettings('deletedDate', new Date());

      default:
    }
  }

  function handleRefuse() {
    setAlert('');
  }

  if (!visible) return null;

  if (muteModal)
    return (
      <Col
        className="chat-mute-settings"
        pos="absolute"
        right={isMobile ? '0' : 'unset'}
        ref={compRef}
      >
        <MuteOptions
          setVisible={setMuteModal}
          updateChatSettings={updateChatSettings}
          chatSettings={chatSettings}
        />
      </Col>
    );

  return (
    <React.Fragment>
      <Clickable goThrough onClick={() => setVisible(false)}>
        <CloseMenu />
      </Clickable>

      {!!alert.length && <Alert text={alert} accept={handleAccept} refuse={handleRefuse} />}

      <Col className="chat-settings" pos="absolute" right={isMobile ? '0' : 'unset'}>
        <Wrapper isMobile={isMobile} className="chat-settings">
          <Clickable onClick={handleMuteModal}>
            <Row start pad="12px 0" between marg="0 0 4px" noFlex>
              <Row start center noFlex>
                <Col noFlex wid="24px" ht="24px" centerAll>
                  <Icon type="mute" col={theme.GREY_60} dimensions={20} />
                </Col>

                <Row start wid="200px" marg="-1px 0 0 8px">
                  <Text b1 med>
                    {t('chat.muteNotifications')}
                  </Text>
                </Row>
              </Row>

              <Row pad="9px 8.8px 0 0" noFlex centerAll>
                <Icon type="rightChevron" dimensions={7.41} col={theme.GREY_60} />
              </Row>
            </Row>
          </Clickable>

          <Divider />

          <Clickable
            onClick={() => {
              updateChatSettings('favorite', !chatSettings.favorite);
            }}
          >
            <Row start pad="12px 0" center marg="4px 0 0">
              <Col noFlex wid="24px" ht="24px" centerAll>
                <Icon type="starOutline" col={theme.GREY_60} dimensions={20} />
              </Col>

              <Row start wid="200px" marg="-2px 0 0 8px">
                <Text b1 med>
                  {chatSettings?.favorite ? t('chat.removeFavorites') : t('chat.addFavorites')}
                </Text>
              </Row>
            </Row>
          </Clickable>

          <Row start pad="12px 0" center>
            <Col noFlex wid="24px" ht="24px" centerAll>
              <Icon type="search" col={theme.GREY_60} dimensions={18} />
            </Col>

            <Row start wid="200px" marg="-2px 0 0 8px">
              <Text b1 med>
                {t('common.search')}
              </Text>
            </Row>
          </Row>

          <Clickable
            onClick={() => {
              updateChatSettings('hidden', !chatSettings.hidden);
            }}
          >
            <Row start pad="12px 0" center>
              <Col noFlex wid="24px" ht="24px" centerAll>
                <Icon type="hide" col={theme.GREY_60} dimensions={24} />
              </Col>

              <Row start wid="200px" marg="-2px 0 0 8px">
                <Text b1 med>
                  {chatSettings?.hidden ? t('chat.unhideChat') : t('chat.hideChat')}
                </Text>
              </Row>
            </Row>
          </Clickable>

          <Clickable onClick={() => alertUser(t('chat.clearConfirmation'))}>
            <Row start pad="12px 0" center>
              <Col noFlex wid="24px" ht="24px" centerAll>
                <Icon type="clean" col={theme.GREY_60} dimensions={22} />
              </Col>

              <Row start wid="200px" marg="-2px 0 0 8px">
                <Text b1 med>
                  {t('chat.clearChatHistory')}
                </Text>
              </Row>
            </Row>
          </Clickable>

          <Row start pad="12px 0" center>
            <Col noFlex wid="24px" ht="24px" centerAll>
              <Icon type="block" col={theme.GREY_60} dimensions={20} />
            </Col>

            <Row start wid="200px" marg="-2px 0 0 8px">
              <Text b1 med>
                {t('chat.block')}
              </Text>
            </Row>
          </Row>

          <Clickable onClick={() => alertUser(t('chat.deleteConfirmation'))}>
            <Row start pad="12px 0" center>
              <Col noFlex wid="24px" ht="24px" centerAll>
                <Icon type="delete" col={theme.GREY_60} dimensions={18} />
              </Col>

              <Row start wid="200px" marg="-2px 0 0 8px">
                <Text b1 med>
                  {t('chat.deleteConversation')}
                </Text>
              </Row>
            </Row>
          </Clickable>
        </Wrapper>
      </Col>
    </React.Fragment>
  );
};

export default Menu;
